import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../apiService';
import { Modalidade } from '../../types';
import { LoadingScreen } from '../components/ui/LoadingScreen';
import { ErrorState } from '../components/ui/ErrorState';

// ============================================================================
// VALIDAÇÃO DE CPF
// ============================================================================
const CPF_REGEX = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;

function isValidCPF(cpf: string): boolean {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(digits)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
    let check = 11 - (sum % 11);
    if (check >= 10) check = 0;
    if (check !== parseInt(digits[9])) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
    check = 11 - (sum % 11);
    if (check >= 10) check = 0;
    return check === parseInt(digits[10]);
}

function formatCPF(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}


interface EventData {
    id: string;
    title: string;
    description: string;
    date: string;
    location: string;
    image: string;
    status: string;
    is_registration_open: number;
    is_online_only: number;
    photos_url?: string;
    modalidades?: Modalidade[];
}

interface FormData {
    nome: string;
    cpf: string;
    email: string;
    telefone: string;
    municipio: string;
    cargo: string;
    modalidade: string;
    observacoes: string;
    website_url?: string; // Honeypot
}



const GenericInscriptionPage: React.FC<{ onVoltar?: () => void }> = ({ onVoltar }) => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    
    const [event, setEvent] = useState<EventData | null>(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState<FormData>({
        nome: '', cpf: '', email: '', telefone: '',
        municipio: '', cargo: '', modalidade: '', observacoes: '',
        website_url: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [cpfError, setCpfError] = useState('');
    const [municipalityOptions, setMunicipalityOptions] = useState<string[]>([]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (slug) {
            fetchEvent(slug);
        }
    }, [slug]);

    const fetchEvent = async (idOrSlug: string) => {
        setLoading(true);
        const data = await apiService.getEvent(idOrSlug);
        if (data) {
            if (data.status === 'past') {
                setError('Este evento já foi realizado. As inscrições estão encerradas.');
                setLoading(false);
                return;
            }
            setEvent(data);
            // Default modalidades if none provided in DB
            if (!data.modalidades) {
                if (Number(data.is_online_only) === 1) {
                    data.modalidades = [
                        { id: 'online', label: 'Online (Meet)', price: 'Gratuito', free: true, description: 'Participação exclusiva via link de transmissão.' }
                    ];
                } else {
                    data.modalidades = [
                        { id: 'presencial', label: 'Presencial', price: 'Gratuito', free: true, description: 'Participação física no local indicado.' }
                    ];
                }
            }

            // Auto-select if only one modality exists
            if (data.modalidades && data.modalidades.length === 1) {
                setFormData(prev => ({ ...prev, modalidade: data.modalidades![0].id }));
            }
        } else {
            setError('Evento não encontrado ou indisponível.');
        }
        setLoading(false);

        // Fetch municipalities for dropdown
        const cities = await apiService.getMunicipalityNames();
        if (cities && cities.length > 0) {
            setMunicipalityOptions([...cities, 'Outro']);
        } else {
            // Fallback caso a API falhe miseravelmente
            setMunicipalityOptions(['Arroio do Sal', 'Capão da Canoa', 'Osório', 'Torres', 'Tramandaí', 'Outro']);
        }
    };

    const handleBack = () => {
        if (onVoltar) {
            onVoltar();
        } else {
            navigate('/');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === 'cpf') {
            const formatted = formatCPF(value);
            setFormData(prev => ({ ...prev, cpf: formatted }));
            const raw = formatted.replace(/\D/g, '');
            if (raw.length === 11) {
                setCpfError(isValidCPF(formatted) ? '' : 'CPF inválido. Verifique os dígitos.');
            } else {
                setCpfError('');
            }
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!event) return;

        if (!formData.cpf || formData.cpf.replace(/\D/g, '').length !== 11) {
            setError('CPF é obrigatório.');
            return;
        }
        if (!isValidCPF(formData.cpf)) {
            setError('CPF inválido. Verifique os dígitos.');
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await apiService.registerInscription({
                ...formData,
                evento: event.title,
                event_id: event.id
            });

            if (result.success) {
                setIsSubmitted(true);
            } else {
                setError(result.message || 'Erro ao realizar inscrição.');
            }
        } catch (err) {
            setError('Erro inesperado no servidor. Tente novamente mais tarde.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <LoadingScreen message="Carregando detalhes do evento..." />;
    }

    if (error && !event) {
        return (
            <ErrorState title="Ops!" description={error}>
                <button onClick={handleBack} className="bg-brand-green text-white px-8 py-3 rounded-full font-bold">
                    Voltar ao Início
                </button>
            </ErrorState>
        );
    }

    if (isSubmitted && event) {
        const modalidade = event.modalidades?.find(m => m.id === formData.modalidade);
        return (
            <SuccessState 
                nome={formData.nome} 
                event={event} 
                modalidadeLabel={modalidade?.label} 
                handleBack={handleBack} 
            />
        );
    }

    if (event && event.is_registration_open === 0) {
        return (
            <ErrorState
                title="Inscrições Encerradas"
                description="As inscrições para este evento não estão mais disponíveis no momento."
            >
                <button onClick={handleBack} className="bg-brand-green text-white px-8 py-3 rounded-full font-bold">
                    Voltar
                </button>
            </ErrorState>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-brand-darkGreen to-brand-green relative overflow-hidden font-sans">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(254,193,14,0.15)_0%,rgba(0,0,0,0)_70%)] pointer-events-none"></div>
            
            <header className="py-6 px-4 relative z-20">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group">
                        <i className="fas fa-arrow-left group-hover:-translate-x-1 transition-transform"></i>
                        <span>Voltar</span>
                    </button>
                    <img src="/assets/logo_litoral.png" alt="ACONTURS Litoral" className="h-12 md:h-16 object-contain" />
                    <div className="w-10"></div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-8 relative z-10">
                <div className="max-w-4xl mx-auto">
                    {/* Event Banner */}
                    <EventBanner event={event} />

                    <div className="bg-white rounded-[2.5rem] shadow-2xl p-6 md:p-12 border border-white/20">
                        <div className="text-center mb-10">
                            <h2 className="text-3xl font-black text-brand-darkGreen mb-2">Formulário de Inscrição</h2>
                            <p className="text-slate-500 font-medium">Garanta sua participação preenchendo os dados abaixo.</p>
                            <div className="w-20 h-1.5 bg-brand-yellow mx-auto mt-4 rounded-full"></div>
                        </div>

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-xl mb-8 flex items-center gap-3 animate-shake">
                                <i className="fas fa-exclamation-circle text-xl"></i>
                                <span className="font-semibold">{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="md:col-span-2 group">
                                    <label className="block text-sm font-black text-slate-700 mb-2 transition-colors group-focus-within:text-brand-green">
                                        Nome Completo *
                                    </label>
                                    <input
                                        type="text"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-5 py-4 rounded-2xl border-2 border-slate-100 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10 outline-none transition-all placeholder:text-slate-300 font-medium"
                                        placeholder="Ex: João da Silva Santos"
                                    />
                                </div>

                                {/* Honeypot - bot trap */}
                                <div className="hidden">
                                    <input 
                                        type="text" 
                                        name="website_url" 
                                        value={formData.website_url} 
                                        onChange={handleChange} 
                                        tabIndex={-1} 
                                        autoComplete="off" 
                                    />
                                </div>

                                <div className="group">
                                    <label className="block text-sm font-black text-slate-700 mb-2 group-focus-within:text-brand-green">
                                        CPF *
                                    </label>
                                    <input
                                        type="text"
                                        name="cpf"
                                        value={formData.cpf}
                                        onChange={handleChange}
                                        required
                                        maxLength={14}
                                        className={`w-full px-5 py-4 rounded-2xl border-2 outline-none transition-all font-mono tracking-widest ${cpfError 
                                            ? 'border-red-200 focus:border-red-500 focus:ring-4 focus:ring-red-100' 
                                            : 'border-slate-100 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10'}`}
                                        placeholder="000.000.000-00"
                                    />
                                    {cpfError && <p className="mt-2 text-xs text-red-500 font-bold uppercase italic"><i className="fas fa-times-circle mr-1"></i>{cpfError}</p>}
                                </div>

                                <div className="group">
                                    <label className="block text-sm font-black text-slate-700 mb-2 group-focus-within:text-brand-green">
                                        WhatsApp *
                                    </label>
                                    <input
                                        type="tel"
                                        name="telefone"
                                        value={formData.telefone}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-5 py-4 rounded-2xl border-2 border-slate-100 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10 outline-none transition-all font-medium"
                                        placeholder="(51) 99999-9999"
                                    />
                                </div>

                                <div className="group">
                                    <label className="block text-sm font-black text-slate-700 mb-2 group-focus-within:text-brand-green">
                                        E-mail *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-5 py-4 rounded-2xl border-2 border-slate-100 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10 outline-none transition-all font-medium"
                                        placeholder="seu@contato.com"
                                    />
                                </div>

                                <div className="group">
                                    <label className="block text-sm font-black text-slate-700 mb-2 group-focus-within:text-brand-green">
                                        Município *
                                    </label>
                                    <select
                                        name="municipio"
                                        value={formData.municipio}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-5 py-4 rounded-2xl border-2 border-slate-100 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10 outline-none transition-all bg-white font-medium"
                                    >
                                        <option value="">Selecione...</option>
                                        {municipalityOptions.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-black text-slate-700 mb-4">Escolha sua Modalidade *</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {event?.modalidades?.map(mod => (
                                            <label 
                                                key={mod.id}
                                                className={`relative flex flex-col p-5 rounded-2xl border-2 cursor-pointer transition-all group/card ${formData.modalidade === mod.id 
                                                    ? 'border-brand-green bg-brand-green/5 ring-4 ring-brand-green/5 scale-[1.02]' 
                                                    : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'}`}
                                            >
                                                <input 
                                                    type="radio" 
                                                    name="modalidade" 
                                                    value={mod.id} 
                                                    checked={formData.modalidade === mod.id}
                                                    onChange={handleChange}
                                                    required
                                                    className="sr-only"
                                                />
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className={`font-black uppercase tracking-wider text-xs ${formData.modalidade === mod.id ? 'text-brand-green' : 'text-slate-500'}`}>
                                                        {mod.free ? 'Gratuito' : `R$ ${mod.price}`}
                                                    </span>
                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${formData.modalidade === mod.id ? 'border-brand-green bg-brand-green shadow-inner' : 'border-slate-200 bg-white'}`}>
                                                        {formData.modalidade === mod.id && <i className="fas fa-check text-[10px] text-white"></i>}
                                                    </div>
                                                </div>
                                                <span className="font-bold text-slate-800 text-lg leading-tight mb-1">{mod.label}</span>
                                                {mod.description && <p className="text-xs text-slate-500 font-medium">{mod.description}</p>}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="md:col-span-2 group">
                                    <label className="block text-sm font-black text-slate-700 mb-2 group-focus-within:text-brand-green">
                                        Cargo ou Função *
                                    </label>
                                    <select
                                        name="cargo"
                                        value={formData.cargo}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-5 py-4 rounded-2xl border-2 border-slate-100 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10 outline-none transition-all bg-white font-medium shadow-sm"
                                    >
                                        <option value="">Selecione seu cargo...</option>
                                        <option value="Conselheiro(a) Tutelar Titular">Conselheiro(a) Tutelar Titular</option>
                                        <option value="Conselheiro(a) Tutelar Suplente">Conselheiro(a) Tutelar Suplente</option>
                                        <option value="Ex-Conselheiro(a) Tutelar">Ex-Conselheiro(a) Tutelar</option>
                                        <option value="Secretário(a) Executivo(a)">Secretário(a) Executivo(a)</option>
                                        <option value="Rede de Apoio (CRAS / CREAS / Saúde / Educação)">Rede de Apoio (CRAS / CREAS / Saúde / Educação)</option>
                                        <option value="Autoridade Municipal (Prefeito / Vereador / Secretário)">Autoridade Municipal (Prefeito / Vereador / Secretário)</option>
                                        <option value="Rede de Proteção (Segurança / Justiça / MP)">Rede de Proteção (Segurança / Justiça / MP)</option>
                                        <option value="Estudante / Acadêmico">Estudante / Acadêmico</option>
                                        <option value="Outros">Outros</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2 group">
                                    <label className="block text-sm font-black text-slate-700 mb-2 group-focus-within:text-brand-green">
                                        Observações Adicionais
                                    </label>
                                    <textarea
                                        name="observacoes"
                                        value={formData.observacoes}
                                        onChange={handleChange}
                                        rows={3}
                                        className="w-full px-5 py-4 rounded-2xl border-2 border-slate-100 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10 outline-none transition-all resize-none font-medium"
                                        placeholder="Alguma necessidade especial ou comentário?"
                                    />
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !!cpfError}
                                    className="w-full bg-brand-green text-white py-5 rounded-[2rem] font-black text-xl hover:bg-brand-darkGreen hover:shadow-2xl hover:-translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 flex items-center justify-center gap-3 active:scale-95 shadow-xl"
                                >
                                    {isSubmitting ? (
                                        <><i className="fas fa-spinner fa-spin text-2xl"></i> PROCESSANDO...</>
                                    ) : (
                                        <><i className="fas fa-id-card text-2xl"></i> CONFIRMAR MINHA VAGA</>
                                    )}
                                </button>
                                <p className="text-center text-slate-400 text-[10px] mt-4 font-bold uppercase tracking-widest">
                                    Ao clicar, você concorda com nossos termos de privacidade e uso de dados.
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </main>

            <footer className="py-12 text-center text-white/40 text-xs font-bold uppercase tracking-widest">
                <p>© 2026 ACONTURS Litoral • Advanced Inscription Engine v2.0</p>
                <p className="mt-2 text-brand-yellow/50">Desenvolvido com Sarcasmo e Elegância Técnica</p>
            </footer>
        </div>
    );
};

// ============================================================================
// SUB-COMPONENTES AUXILIARES
// ============================================================================

const EventBanner: React.FC<{ event: EventData | null }> = ({ event }) => (
    <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 md:p-8 mb-8 border border-white/20 shadow-2xl overflow-hidden relative animate-fade-in">
        <div className="flex flex-col md:flex-row gap-6 items-center text-white">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden shadow-2xl shrink-0 border-2 border-brand-yellow/30 bg-white/5 flex items-center justify-center">
                {event?.image ? (
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                ) : (
                    <div className="text-center">
                        <div className="text-2xl font-black text-brand-yellow leading-none">ACONTURS</div>
                        <div className="text-[10px] text-white/60 font-bold mt-1 uppercase">Litoral</div>
                    </div>
                )}
            </div>
            <div className="text-center md:text-left flex-1">
                <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-3">
                    <span className="bg-brand-yellow text-brand-darkGreen text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter shadow-sm">
                        EVENTO DINÂMICO
                    </span>
                    <span className="bg-white/20 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase">
                        {event?.location.split('—')[1] || event?.location}
                    </span>
                </div>
                <h1 className="text-2xl md:text-4xl font-black text-white mb-3 tracking-tight">
                    {event?.title}
                </h1>
                <div className="flex flex-col sm:flex-row gap-3 text-white/80 text-sm font-medium">
                    <span className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10">
                        <i className="fas fa-calendar-alt text-brand-yellow"></i>
                        {event?.date}
                    </span>
                    <span className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10">
                                            <i className={`fas ${Number(event?.is_online_only) === 1 ? 'fa-video text-brand-green' : 'fa-map-marker-alt text-brand-red'}`}></i>
                                            {Number(event?.is_online_only) === 1 ? 'Evento Online' : event?.location}
                    </span>
                </div>
            </div>
        </div>
    </div>
);

const SuccessState: React.FC<{ 
    nome: string, 
    event: EventData, 
    modalidadeLabel?: string, 
    handleBack: () => void 
}> = ({ nome, event, modalidadeLabel, handleBack }) => (
    <div className="min-h-screen bg-gradient-to-br from-brand-darkGreen to-brand-green flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-lg w-full text-center animate-scale-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 transition-transform hover:scale-110">
                <i className="fas fa-check text-4xl text-green-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-brand-darkGreen mb-4">Inscrição Confirmada!</h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
                Parabéns, <span className="font-bold text-brand-green">{nome}</span>! <br />
                Sua participação no evento <strong>{event.title}</strong> está garantida.
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left space-y-2 border border-slate-100">
                <p className="text-sm text-slate-500 font-medium">
                    <i className="fas fa-calendar-alt mr-2 text-brand-green w-5"></i>{event.date}
                </p>
                <p className="text-sm text-slate-500 font-medium">
                    <i className={`fas ${Number(event.is_online_only) === 1 ? 'fa-video text-brand-green' : 'fa-map-marker-alt text-brand-red'} mr-2 w-5`}></i>
                    {Number(event.is_online_only) === 1 ? 'Evento Online' : event.location}
                </p>
                {modalidadeLabel && (
                    <p className="text-sm text-slate-500 font-medium">
                        <i className="fas fa-tag mr-2 text-brand-yellow w-5"></i>{modalidadeLabel}
                    </p>
                )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                    href="https://wa.me/5551982755065"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-bold hover:bg-[#128C7E] transition-all shadow-lg active:scale-95"
                >
                    <i className="fab fa-whatsapp text-xl"></i> WhatsApp
                </a>
                <button
                    onClick={handleBack}
                    className="bg-brand-green text-white px-6 py-3 rounded-full font-bold hover:bg-brand-darkGreen transition-all shadow-lg active:scale-95"
                >
                    Voltar ao Site
                </button>
            </div>
        </div>
    </div>
);

export default GenericInscriptionPage;
